/**
 * Axios Interceptor for handling authentication
 *
 * This interceptor handles 401 errors and attempts to refresh the token
 * If token refresh fails, it redirects to the login page
 */

import axios from 'axios';
import Vue from 'vue';
import store from '@/store/panel.js';

// Variables to track token refresh state
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 1;
let hasShownExpiredNotification = false;

// Track if we're in the initial page load phase
let isInitialPageLoad = true;
const INITIAL_PAGE_LOAD_TIMEOUT = 5000; // 5 seconds

// Set a timeout to mark the end of the initial page load phase
setTimeout(() => {
    isInitialPageLoad = false;
    console.log('[🔄] Initial page load phase ended');
}, INITIAL_PAGE_LOAD_TIMEOUT);

// Reset notification flag after a delay
const resetNotificationFlag = () => {
    setTimeout(() => {
        hasShownExpiredNotification = false;
    }, 5000);
};

/**
 * Process the queue of failed requests
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * Show a notification when the session has expired
 */
const showSessionExpiredNotification = () => {
    // If we've already shown a notification, don't show another one
    if (hasShownExpiredNotification) {
        return;
    }

    // Set flag to prevent multiple notifications
    hasShownExpiredNotification = true;

    // Reset the flag after a delay
    resetNotificationFlag();

    try {
        // Check if the page was just loaded (within the last 2 seconds)
        // Using modern Performance API instead of deprecated window.performance.timing
        const pageLoadTime = window.performance &&
            window.performance.getEntriesByType &&
            window.performance.getEntriesByType('navigation').length > 0 ?
            window.performance.getEntriesByType('navigation')[0].startTime : 0;
        const pageJustLoaded = pageLoadTime !== undefined &&
            (Date.now() - (performance.timeOrigin + pageLoadTime) < 2000);

        // If page just loaded, show a different message
        const message = pageJustLoaded
            ? 'Je sessie wordt vernieuwd...'
            : 'Je sessie is verlopen. Je wordt uitgelogd...';

        const type = pageJustLoaded ? 'is-info' : 'is-warning';

        // Use Buefy notification if available
        if (Vue.prototype.$buefy && Vue.prototype.$buefy.notification) {
            Vue.prototype.$buefy.notification.open({
                duration: 5000,
                message: message,
                type: type,
                position: 'is-top',
                queue: false
            })
        } else {
            alert(message);
        }
    } catch (e) {
        console.error('Error showing notification:', e);
    }
};

/**
 * Initialize the Axios interceptor
 */
const setupInterceptors = () => {
    // Request interceptor to add the auth token to every request
    axios.interceptors.request.use(
        config => {
            // Get the token from localStorage
            const token = localStorage.getItem('auth_token');

            // If token exists, add it to the request headers
            if (token) {
                config.headers['Authorization'] = 'Bearer ' + token;
            }

            // Add a flag to indicate if this is during initial page load
            // This helps us handle 401s differently during initial load
            if (isInitialPageLoad) {
                config.headers['X-Initial-Load'] = 'true';
            }

            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    // Response interceptor for handling 401 errors
    axios.interceptors.response.use(
        response => response,
        error => {
            const originalRequest = error.config;

            // If error is 401 and not a refresh token request itself
            const isRefreshEndpoint = originalRequest.url.includes('/refresh-token');
            const isInitialLoadRequest = originalRequest.headers && originalRequest.headers['X-Initial-Load'] === 'true';

            // For initial page load requests that fail with 401, we'll queue them but not show error notifications
            // This prevents the "logged out due to inactivity" message when returning to the app
            if (error.response && error.response.status === 401 && !originalRequest._retry && !isRefreshEndpoint && refreshAttempts < MAX_REFRESH_ATTEMPTS) {
                // If this is an initial load request, we'll be more patient with retries
                if (isInitialLoadRequest) {
                    console.log('[🔄] Initial page load request failed with 401, queueing for retry:', originalRequest.url);
                }
                if (isRefreshing) {
                    // If we're already refreshing, queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return axios(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                // Get the current token from localStorage
                const token = localStorage.getItem('auth_token');

                if (!token) {
                    // No token available, but don't redirect yet
                    console.error('[⚠️] No token available for refresh');

                    // If this is during initial page load, let the app handle it
                    if (isInitialPageLoad) {
                        console.log('[🔄] No token during initial page load, letting app handle it');
                        isRefreshing = false;
                        return Promise.reject(error);
                    }

                    // For non-initial requests, we'll still reject but let the app decide what to do
                    isRefreshing = false;
                    return Promise.reject(error);
                }

                // Increment refresh attempts
                refreshAttempts++;

                // Try to refresh the token
                return new Promise((resolve, reject) => {
                    axios.post('/api/refresh-token', { token: token })
                        .then(response => {
                            if (response.data.token) {
                                // Update token in localStorage
                                localStorage.setItem('auth_token', response.data.token);

                                // Update axios headers for this request
                                originalRequest.headers['Authorization'] = 'Bearer ' + response.data.token;

                                // Also update the store with the new token
                                store.commit('setToken', response.data.token);

                                // Process the queue with the new token
                                processQueue(null, response.data.token);

                                // Resolve the original request
                                resolve(axios(originalRequest));
                            } else {
                                // No new token received, but don't force logout yet
                                processQueue(error, null);
                                console.error('[❌] No token received from refresh endpoint');

                                // Let the app handle this situation
                                // Only show notification if not in initial page load
                                if (!isInitialPageLoad) {
                                    showSessionExpiredNotification();
                                }

                                reject(error);
                            }
                        })
                                                .catch(err => {
                            // Token refresh failed
                            processQueue(err, null);
                            console.error('[❌] Token refresh failed:', err);

                            // Check if this is a blacklisted token error
                            const isBlacklistedError = err.response &&
                                err.response.data &&
                                (err.response.data.error === 'The token has been blacklisted' ||
                                 err.response.data.message === 'The token has been blacklisted');

                            if (isBlacklistedError) {
                                console.warn('[⚠️] Token has been blacklisted, clearing token and redirecting to login');
                                // Clear user data from store
                                store.commit('clearUserData');

                                // Redirect to login page
                                window.location.href = '/login?blacklisted=true';
                                return;
                            }

                            // During initial page load, let the app handle it
                            if (isInitialPageLoad) {
                                console.log('[🔄] Token refresh failed during initial page load, letting app handle it');
                                reject(err);
                                return;
                            }

                            // For non-initial requests, show notification but don't force logout
                            // The app will handle the logout if needed
                            showSessionExpiredNotification();
                            reject(err);
                        })
                        .finally(() => {
                            isRefreshing = false;

                            // If we've reached max attempts and still failed, reset counter for next time
                            if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
                                refreshAttempts = 0;
                            }
                        });
                });
            }

            // Handle 401 errors that weren't handled by the token refresh process
            // This happens when we've reached max attempts or it's a refresh token request itself
            if (error.response && error.response.status === 401 &&
                (refreshAttempts >= MAX_REFRESH_ATTEMPTS || originalRequest.url.includes('/refresh-token'))) {

                // Always suppress forced logout during initial page load
                // Let the app's normal token refresh logic handle it
                if (isInitialPageLoad) {
                    console.log('[🔄] Suppressing logout during initial page load, waiting for app to handle it');
                    // Reset counter but don't redirect yet
                    refreshAttempts = 0;
                    return Promise.reject(error);
                }

                // For refresh token requests that fail, we should let the app handle it
                if (originalRequest.url.includes('/refresh-token')) {
                    console.log('[❌] Refresh token request failed, letting app handle it');
                    refreshAttempts = 0;
                    return Promise.reject(error);
                }

                // Reset counter for next time
                refreshAttempts = 0;

                // Only show notification, don't force logout
                // The app will handle logout if needed
                showSessionExpiredNotification();
            }

            return Promise.reject(error);
        }
    );
};

export default setupInterceptors;
